import { useState } from "react";

import {
  useCreateGongIntegrationMutation,
  useDeleteGongIntegrationMutation,
  useGongIntegrationsQuery,
} from "@/graphql";

import {
  formatDate,
  formatUserName,
  getStatusLabel,
  type IntegrationInstance,
} from "./shared-utils";

export function useGongIntegration() {
  // State
  const [integrationName, setIntegrationName] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [accessKeySecret, setAccessKeySecret] = useState("");
  const [connectionErrors, setConnectionErrors] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  // GraphQL hooks
  const {
    data: integrationsData,
    loading: integrationsLoading,
    refetch: refetchIntegrations,
  } = useGongIntegrationsQuery();

  const [createIntegration, { loading: createLoading }] =
    useCreateGongIntegrationMutation();
  const [deleteIntegration] = useDeleteGongIntegrationMutation();

  // Computed values
  const integrations = integrationsData?.gongIntegrations || [];
  const hasIntegrations = integrations.length > 0;
  const statusLabel = getStatusLabel(hasIntegrations, integrations.length);

  const instances: IntegrationInstance[] = integrations
    .filter(
      (integration): integration is NonNullable<typeof integration> =>
        integration != null,
    )
    .map(
      (integration): IntegrationInstance => ({
        id: integration.id || "",
        name: integration.name || "Unnamed",
        enabledBy: {
          name: integration.integratedBy
            ? formatUserName(
                integration.integratedBy.firstName,
                integration.integratedBy.lastName,
                integration.integratedBy.email,
              )
            : "Unknown",
          avatar: integration.integratedBy?.photo ?? undefined,
        },
        enabledOn: formatDate(integration.createdAt),
      }),
    );

  // Actions
  const openModal = () => {
    setConnectionErrors([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIntegrationName("");
    setApiUrl("");
    setAccessKey("");
    setAccessKeySecret("");
    setConnectionErrors([]);
  };

  const createGongIntegration = async (values: Record<string, string>) => {
    setConnectionErrors([]);

    try {
      const result = await createIntegration({
        variables: {
          name: values["integration-name"],
          apiUrl: values["api-url"],
          accessKey: values["access-key"],
          accessKeySecret: values["access-key-secret"],
        },
      });

      if (result.data?.createGongIntegration?.ok) {
        closeModal();
        await refetchIntegrations();
        return true;
      } else {
        const errors = result.data?.createGongIntegration?.errors || [];
        const errorMessages = errors
          .map((error) => error?.message)
          .filter((message): message is string => Boolean(message));
        setConnectionErrors(errorMessages);
        return false;
      }
    } catch (error: unknown) {
      console.error("Failed to create Gong integration:", error);

      setConnectionErrors([
        "Failed to connect to Gong. Please check your credentials.",
      ]);
      return false;
    }
  };

  const deleteGongIntegration = async (instanceId: string) => {
    try {
      await deleteIntegration({
        variables: { integrationId: instanceId },
      });
      await refetchIntegrations();
      return true;
    } catch (error) {
      console.error("Failed to delete Gong integration:", error);
      return false;
    }
  };

  return {
    // State
    integrationName,
    setIntegrationName,
    apiUrl,
    setApiUrl,
    accessKey,
    setAccessKey,
    accessKeySecret,
    setAccessKeySecret,
    connectionErrors,
    isModalOpen,
    isFlyoutOpen,
    setIsFlyoutOpen,

    // Data
    integrations,
    hasIntegrations,
    instances,
    statusLabel,

    // Loading states
    integrationsLoading,
    createLoading,

    // Actions
    openModal,
    closeModal,
    createIntegration: createGongIntegration,
    deleteIntegration: deleteGongIntegration,
  };
}
